import { getLocale, INTL_LOCALES, tx, type Locale } from "./i18n";
import { readable, resultLabel, statusText } from "./localized-text";
import type {
  Cell,
  QueryResult,
  ResultField,
  VisualizationConfig,
} from "./types";
export function formatValue(
  value: Cell | undefined,
  format: VisualizationConfig["format"] = "number",
  locale: Locale = getLocale(),
  field = "",
) {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "number" && !Number.isFinite(value))
  )
    return tx("Not available", {}, locale);
  if (typeof value === "string") return statusText(value, field, locale);
  if (typeof value === "boolean") return tx(value ? "Yes" : "No", {}, locale);
  if (typeof value === "object") return JSON.stringify(value);
  if (format === "currency")
    return new Intl.NumberFormat(INTL_LOCALES[locale], {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  if (format === "percent" || format === "ratio")
    return new Intl.NumberFormat(INTL_LOCALES[locale], {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(format === "ratio" ? value : value / 100);
  const number = new Intl.NumberFormat(INTL_LOCALES[locale], {
    maximumFractionDigits: 1,
  }).format(value);
  return format === "minutes"
    ? `${number} ${tx("min", {}, locale)}`
    : format === "hours"
      ? `${number} ${tx("h", {}, locale)}`
      : number;
}
export function label(field: string) {
  return tx(field === "kpi" ? "KPI" : readable(field));
}
export function csvText(
  result: QueryResult,
  fieldLabels?: Record<string, string>,
  literalLabels = new Set<string>(),
): string {
  const escape = (value: Cell | undefined) => {
    let text =
      value === null || value === undefined
        ? ""
        : typeof value === "object"
          ? JSON.stringify(value)
          : String(value);
    // CSVs are opened in spreadsheet applications; authored query text must stay text.
    if (typeof value === "string" && /^[=+@-]/.test(text)) text = `'${text}`;
    return `"${text.replaceAll('"', '""')}"`;
  };
  return [
    result.columns
      .map((column) => {
        const field = result.fields?.find((field) => field.name === column);
        const unit =
          field?.format === "hours"
            ? "h"
            : field?.format === "minutes"
              ? "min"
              : field?.format === "percent" || field?.format === "ratio"
                ? "%"
                : field?.format === "currency"
                  ? "EUR"
                  : "";
        return escape(
          (field
            ? resultLabel(
                field,
                fieldLabels?.[column],
                literalLabels.has(column) ||
                  (fieldLabels?.[column] !== undefined &&
                    fieldLabels[column] !== field.label),
              )
            : (fieldLabels?.[column] ?? column)) + (unit ? ` (${unit})` : ""),
        );
      })
      .join(","),
    ...result.rows.map((row) =>
      result.columns
        .map((column) => {
          const value = row[column];
          const field = result.fields.find((item) => item.name === column);
          return escape(
            field?.format === "ratio" && typeof value === "number"
              ? value * 100
              : value,
          );
        })
        .join(","),
    ),
  ].join("\r\n");
}
export function csvFilename(name: string) {
  const base = name
    .normalize("NFC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-|-$/g, "");
  return `${base || "report"}.csv`;
}
export function downloadCsv(
  result: QueryResult,
  name: string,
  fieldLabels?: Record<string, string>,
  literalLabels?: Set<string>,
) {
  const url = URL.createObjectURL(
    new Blob(["\uFEFF", csvText(result, fieldLabels, literalLabels)], {
      type: "text/csv;charset=utf-8",
    }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = csvFilename(name);
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function shortDate(
  date: string,
  locale: Locale = getLocale(),
  withYear = false,
) {
  const month = /^\d{4}-\d{2}$/.test(date);
  return new Date(
    `${month ? `${date}-01` : date.slice(0, 10)}T12:00:00Z`,
  ).toLocaleDateString(
    INTL_LOCALES[locale],
    month
      ? { month: "short", year: "numeric" }
      : {
          day: "numeric",
          month: "short",
          ...(withYear ? { year: "numeric" } : {}),
        },
  );
}

export function dateRange(
  from: string,
  to: string,
  locale: Locale = getLocale(),
) {
  return new Intl.DateTimeFormat(INTL_LOCALES[locale], {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).formatRange(new Date(`${from}T12:00:00Z`), new Date(`${to}T12:00:00Z`));
}

/** Use schema types, rather than column spelling, to format date and timestamp cells. */
export function formatResultValue(
  value: Cell | undefined,
  field?: ResultField,
  format?: VisualizationConfig["format"],
) {
  if (
    typeof value === "string" &&
    field &&
    ["date", "timestamp"].includes(field.type)
  ) {
    const date = new Date(
      field.type === "date" ? `${value.slice(0, 10)}T12:00:00Z` : value,
    );
    if (Number.isFinite(date.getTime()))
      return field.type === "date"
        ? shortDate(value)
        : new Intl.DateTimeFormat(INTL_LOCALES[getLocale()], {
            dateStyle: "medium",
            timeStyle: "short",
            timeZone: "UTC",
          }).format(date) + " UTC";
  }
  return formatValue(value, format ?? field?.format, undefined, field?.name);
}
